/**
 * フォーカス状態デバッグ用コンポーネント
 * 開発時のキーボードナビゲーション確認に使用
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Badge,
  Stack,
  Group,
  Paper,
  Title,
  Code,
  Switch,
  Button,
} from '@mantine/core';
import { IconKeyboard, IconEye, IconTarget } from '@tabler/icons-react';

interface FocusDebuggerProps {
  enabled?: boolean;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  showOutline?: boolean;
}

export const FocusDebugger: React.FC<FocusDebuggerProps> = ({
  enabled = process.env.NODE_ENV === 'development',
  position = 'bottom-right',
  showOutline = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentFocus, setCurrentFocus] = useState<{
    element: string;
    tagName: string;
    id?: string;
    className?: string;
    tabIndex?: number;
    attributes: string[];
  } | null>(null);
  const [focusHistory, setFocusHistory] = useState<string[]>([]);
  const [outlineEnabled, setOutlineEnabled] = useState(showOutline);

  // フォーカス状態の監視
  useEffect(() => {
    if (!enabled) return;

    const handleFocusChange = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const focusInfo = {
        element: target.outerHTML.substring(0, 100) + (target.outerHTML.length > 100 ? '...' : ''),
        tagName: target.tagName.toLowerCase(),
        id: target.id,
        className: target.className,
        tabIndex: target.tabIndex,
        attributes: Array.from(target.attributes).map(attr => `${attr.name}="${attr.value}"`),
      };

      setCurrentFocus(focusInfo);
      setFocusHistory(prev => [
        `${new Date().toLocaleTimeString()}: ${target.tagName.toLowerCase()}${target.id ? `#${target.id}` : ''}${target.className ? `.${target.className.split(' ')[0]}` : ''}`,
        ...prev.slice(0, 9) // 最新10件を保持
      ]);
    };

    // フォーカスイベントリスナー
    document.addEventListener('focusin', handleFocusChange);
    document.addEventListener('focusout', handleFocusChange);

    // 初期フォーカス状態を取得
    if (document.activeElement) {
      handleFocusChange({ target: document.activeElement } as FocusEvent);
    }

    return () => {
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('focusout', handleFocusChange);
    };
  }, [enabled]);

  // フォーカスアウトラインのスタイル注入
  useEffect(() => {
    if (!enabled) return;

    const styleId = 'focus-debugger-outline';
    let style = document.getElementById(styleId) as HTMLStyleElement;

    if (outlineEnabled) {
      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }

      style.textContent = `
        *:focus {
          outline: 3px solid #3b82f6 !important;
          outline-offset: 2px !important;
          box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.3) !important;
        }
        
        *:focus-visible {
          outline: 3px solid #10b981 !important;
          outline-offset: 2px !important;
          box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.3) !important;
        }
      `;
    } else if (style) {
      style.remove();
    }

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [enabled, outlineEnabled]);

  // キーボードショートカット（Ctrl/Cmd + Shift + F でトグル）
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') {
        e.preventDefault();
        setIsVisible(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);

  if (!enabled) return null;

  const positionStyles: Record<typeof position, React.CSSProperties> = {
    'top-left': { top: 16, left: 16 },
    'top-right': { top: 16, right: 16 },
    'bottom-left': { bottom: 16, left: 16 },
    'bottom-right': { bottom: 16, right: 16 },
  };

  return (
    <>
      {/* トグルボタン */}
      <Box
        style={{
          position: 'fixed',
          ...positionStyles[position],
          zIndex: 9999,
        }}
      >
        <Button
          size="xs"
          variant="filled"
          color="blue"
          leftSection={<IconKeyboard size={14} />}
          onClick={() => setIsVisible(!isVisible)}
          style={{
            opacity: isVisible ? 1 : 0.7,
            transition: 'opacity 0.2s ease',
          }}
          title="フォーカスデバッガー (Ctrl/Cmd + Shift + F)"
        >
          Focus
        </Button>
      </Box>

      {/* デバッグパネル */}
      {isVisible && (
        <Paper
          shadow="lg"
          p="sm"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10000,
            maxWidth: '500px',
            width: '90vw',
            maxHeight: '70vh',
            overflowY: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Stack gap="sm">
            {/* ヘッダー */}
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <IconTarget size={20} />
                <Title order={5}>フォーカスデバッガー</Title>
              </Group>
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => setIsVisible(false)}
              >
                ×
              </Button>
            </Group>

            {/* コントロール */}
            <Group gap="md">
              <Switch
                label="フォーカスアウトライン"
                checked={outlineEnabled}
                onChange={(e) => setOutlineEnabled(e.currentTarget.checked)}
                size="sm"
              />
              <Badge variant="light" color="blue" leftSection={<IconEye size={12} />}>
                Ctrl/Cmd + Shift + F
              </Badge>
            </Group>

            {/* 現在のフォーカス */}
            <Box>
              <Text size="sm" fw={600} mb="xs">現在のフォーカス:</Text>
              {currentFocus ? (
                <Stack gap="xs">
                  <Group gap="xs">
                    <Badge variant="filled" size="sm">
                      {currentFocus.tagName}
                    </Badge>
                    {currentFocus.id && (
                      <Badge variant="light" color="green" size="sm">
                        #{currentFocus.id}
                      </Badge>
                    )}
                    {currentFocus.className && (
                      <Badge variant="light" color="blue" size="sm">
                        .{currentFocus.className.split(' ')[0]}
                      </Badge>
                    )}
                    <Badge variant="light" color="gray" size="sm">
                      tabIndex: {currentFocus.tabIndex}
                    </Badge>
                  </Group>
                  <Code
                    block
                    style={{
                      fontSize: '11px',
                      maxHeight: '60px',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {currentFocus.element}
                  </Code>
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">フォーカスされている要素がありません</Text>
              )}
            </Box>

            {/* フォーカス履歴 */}
            <Box>
              <Text size="sm" fw={600} mb="xs">フォーカス履歴:</Text>
              <Stack gap="xs">
                {focusHistory.length > 0 ? (
                  focusHistory.map((entry, index) => (
                    <Code
                      key={index}
                      style={{
                        fontSize: '10px',
                        opacity: 1 - (index * 0.1),
                        backgroundColor: index === 0 ? 'rgba(59, 130, 246, 0.1)' : undefined,
                      }}
                    >
                      {entry}
                    </Code>
                  ))
                ) : (
                  <Text size="xs" c="dimmed">履歴がありません</Text>
                )}
              </Stack>
            </Box>

            {/* ヘルプ */}
            <Box>
              <Text size="sm" fw={600} mb="xs">キーボードナビゲーション:</Text>
              <Stack gap="xs">
                <Group gap="xs">
                  <Badge variant="outline" size="xs">Tab</Badge>
                  <Text size="xs">次の要素へ</Text>
                </Group>
                <Group gap="xs">
                  <Badge variant="outline" size="xs">Shift + Tab</Badge>
                  <Text size="xs">前の要素へ</Text>
                </Group>
                <Group gap="xs">
                  <Badge variant="outline" size="xs">Enter</Badge>
                  <Text size="xs">アクティベート/送信</Text>
                </Group>
                <Group gap="xs">
                  <Badge variant="outline" size="xs">Space</Badge>
                  <Text size="xs">選択/チェック</Text>
                </Group>
                <Group gap="xs">
                  <Badge variant="outline" size="xs">Escape</Badge>
                  <Text size="xs">キャンセル/閉じる</Text>
                </Group>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      )}
    </>
  );
};

export default FocusDebugger;